import { createRouter, createWebHistory } from "vue-router";
import LandingPage from "@/views/LandingPage.vue";
import NotFound from "@/components/404NotFound.vue";
import VueCookies from "vue-cookies";
import store from "@/store";
import { toast } from "vue3-toastify";

import ServerError from "@/components/500InternalServerError.vue";
import NetworkErr from "@/components/NetworkError.vue";

const routes = [
  {
    path: "/",
    name: "landingPage",
    component: LandingPage,
    meta: { title: "سامانه استخدام", requiresAuth: false },
  },
  {
    path: "/login",
    name: "login",
    component: () =>
      import(/* webpackChunkName: "login" */ "@/views/LoginPage.vue"),
    beforeEnter: () => {
      if (store.getters.getIsAuthenticated) {
        return { name: "dashboard" };
      }
    },
    meta: { requiresAuth: false },
  },
  {
    path: "/register",
    name: "register",
    component: () =>
      import(/* webpackChunkName: "register" */ "@/views/RegisterPage.vue"),
    beforeEnter: () => {
      if (store.getters.getIsAuthenticated) {
        return { name: "dashboard" };
      }
    },
    meta: { requiresAuth: false },
  },
  {
    path: "/forget",
    name: "forget",
    component: () =>
      import(/* webpackChunkName: "forget" */ "@/views/ForgetPasswordPage.vue"),
    meta: { requiresAuth: false },
  },
  {
    path: "/user",
    component: () =>
      import(/* webpackChunkName: "user" */ "@/views/UserPage.vue"),
    children: [
      {
        path: "",
        name: "dashboard",
        meta: { requiresAuth: true },
        redirect: { name: 'jobs' }
      },
      {
        path: "jobs",
        name: "jobs",
        component: () =>
          import(
            /* webpackChunkName: "jobs" */ "@/views/userPages/JobsPage.vue"
          ),
        meta: { requiresAuth: false },
      },
      {
        path: "jobs-detail/:id",
        name: "jobsDetail",
        component: () =>
          import(
            /* webpackChunkName: "jobsDetail" */ "@/views/userPages/JobsDetailPage.vue"
          ),
        meta: { requiresAuth: false },
      },
      {
        path: "requests",
        name: "requests",
        component: () =>
          import(
            /* webpackChunkName: "requests" */ "@/views/userPages/RequestsPage.vue"
          ),
        meta: { requiresAuth: true },
      },
      {
        path: "request-job/:positionId/:status/:requestId",
        name: "requestJob",
        component: () =>
          import(
            /* webpackChunkName: "requestJob" */ "@/views/userPages/RequestJobPage.vue"
          ),
        meta: { requiresAuth: true },
      },
      {
        path: "profile",
        name: "profile",
        component: () =>
          import(
            /* webpackCunkName: "profile" */ "@/views/userPages/ProfilePage.vue"
          ),
        children: [
          {
            path: "personal",
            name: "profile-personal",
            component: () =>
              import(
                /* webpackCunkName: "personal-info" */ "@/views/userPages/profilePages/PersonalInfoPage.vue"
              ),
            meta: { requiresAuth: true },
          },
          {
            path: "educational",
            name: "profile-educational",
            component: () =>
              import(
                /* webpackCunkName: "educational-info" */ "@/views/userPages/profilePages/EducationalInfoPage.vue"
              ),
            meta: { requiresAuth: true },
          },
          {
            path: "resume",
            name: "profile-resume",
            component: () =>
              import(
                /* webpackCunkName: "resume-info" */ "@/views/userPages/profilePages/ResumePage.vue"
              ),
            meta: { requiresAuth: true },
          },
        ],
      },
      {
        path: "/change-password",
        name: "changePassword",
        component: () =>
          import(
            /* webpackCunkName: "changePassword" */ "@/views/userPages/ChangePasswordPage.vue"
          ),
        meta: { requiresAuth: true },
      },
    ],
  },

  {
    path: "/admin",
    component: () =>
      import(/* webpackChunkName: "adminLayout"*/ "@/views/AdminPage.vue"),
    children: [
      {
        path: "all-requests",
        name: "allRequests",
        component: () =>
          import(
            /* webpackChunkName: "AllRequests" */ "@/views/adminPages/AllRequestsPage.vue"
          ),
        meta: { requiresAuth: true },
      },
      {
        path: "all-users",
        name: "allUsers",
        component: () =>
          import(
            /* webpackChunkName: "AllUsers" */ "@/views/adminPages/AllUsersPage.vue"
          ),
        meta: { requiresAuth: true },
      },
      {
        path: "resume/:id",
        name: "resume",
        component: () =>
          import(
            /* webpackChunkName: "Resume" */ "@/views/adminPages/RequestDetail.vue"
          ),
        meta: { requiresAuth: true },
      }
    ]
  },

  {
    path: '/:pathMatch(.*)*',
    name: '404',
    component: NotFound
  },
  {
    path: '/:pathMatch(.*)*/500',
    name: '500',
    component: ServerError
  },
  {
    path: '/:pathMatch(.*)*/network-err',
    name: 'NetworkErr',
    component: NetworkErr
  }
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
});

router.beforeEach(async (to, from, next) => {
  window.scrollTo(0, 0);
  let authenticated = store.getters.getIsAuthenticated;

  if (VueCookies.isKey("__HIRE-REFRESH-TOKEN") && !authenticated) {
    store.commit("setIsAuthenticated");
    authenticated = true;
  }

  if (from.path != to.path && from.query.username && !to.query.username) {
    return next({
      ...to,
      query: {
        ...to.query,
        username: from.query.username,
      },
      replace: true,
    });
  }


  if (authenticated) {
    let checkRedirect = store.getters.getCheckRedirect;
    if (!checkRedirect && to.meta.requiresAuth) {
      await store.dispatch("getRedirectStatus");
      let needRedirect = store.getters.getNeedRedirect;
      let redirectUrl = store.getters.getRedirectUrl;
      store.commit("setCheckRedirect");
      if (needRedirect && redirectUrl) {
        window.location.href = redirectUrl;
        return;
      } else if (needRedirect) {
        store.dispatch("exitApp");
        return;
      }
    }
    let username = to.query.username;
    store.commit("setUsername", username);
    await store.dispatch("getUserDataFromApi", username);
    const userData = store.getters.getUserData;
    if (to.meta.requiresAuth) {
      if (to.fullPath.includes("profile")) {
        if (to.name == "profile-educational" && !userData.have_personal_info) {
          router.push({
            name: "profile-personal",
            query: { profile: "notComplete", username: from.query.username },
          });
        } else if (to.name == "profile-resume") {
          if (!userData.have_personal_info) {
            router.push({
              name: "profile-personal",
              query: { profile: "notComplete", username: from.query.username },
            });
          } else if (!userData.have_education_catalog) {
            router.push({
              name: "profile-educational",
              query: { profile: "notComplete", username: from.query.username },
            });
          } else next();
        } else next();
      } else if (
        !userData.have_personal_info ||
        !userData.have_education_catalog ||
        !userData.have_experience_catalog
      ) {
        router.push({
          name: "profile-personal",
          query: { profile: "notComplete", username: from.query.username },
        });
      } else next();
    } else next();
  } else {
    if (to.meta.requiresAuth) {
      if (from.name === undefined)
        router.push({
          name: "login",
          // save the location we were at to come back later
          query: { redirect: to.fullPath },
        });
      else {
        toast("ابتدا وارد شوید!");
      }
    } else next();
  }
  if (localStorage.getItem("dark-mode") == "true") {
    store.commit("setDarkMode", true);
  }
});

router.afterEach((to) => {
  if (to.query.profile == "notComplete")
    toast.warning("اطلاعات خود را کامل کنید");
  if (to.query.send == "success")
    toast.success("درخواست شما با موفقیت ارسال شد");
  if (to.query.change == "success")
    toast.success("درخواست شما با موفقیت تغییر یافت");
});

export default router;
